/**
 * AI Relationship-Assist action helpers extracted verbatim from
 * `ContactEditModal.tsx` and parameterized on typed deps.
 *
 * The orchestrator keeps the effect shell (with its `[contact?.id, isNew]`
 * dependency array) and the handler shells, delegating the bodies here so
 * behavior is unchanged (#3293 Pass-2).
 *
 * All three hit the per-workspace crm-engine endpoints through the typed
 * `endpoints/crm-engine` wrappers, which route via the shared http client so
 * the `Authorization`, `x-user-id` and `x-workspace-id` headers (the
 * relationship-engine auth contract) are supplied centrally — and the calls
 * gain the client's 401 refresh-retry (#3268).
 */

import {
  getRelationshipState as fetchRelationshipStateApi,
  analyzeContact as analyzeContactApi,
  draftEmail as draftEmailApi,
} from '@/lib/api/endpoints/crm-engine';
import type { RelationshipState } from '../../RelationshipAssistPanel';
import type { DraftVariant } from '../../DraftVariantSelector';

export interface FetchRelStateDeps {
  contactId: string;
  setRelStateLoading: (b: boolean) => void;
  setRelationshipState: (s: RelationshipState | null) => void;
}

/** Body of the modal-open relationship-state fetch effect. */
export async function fetchRelationshipState(deps: FetchRelStateDeps): Promise<void> {
  const { contactId, setRelStateLoading, setRelationshipState } = deps;
  setRelStateLoading(true);
  try {
    const data = await fetchRelationshipStateApi(contactId);
    setRelationshipState((data.state as RelationshipState | null) ?? null);
  } catch {
    // Best-effort
  } finally {
    setRelStateLoading(false);
  }
}

export interface AnalyzeDeps {
  contactId: string | undefined;
  setAnalyzing: (b: boolean) => void;
  setRelationshipState: (s: RelationshipState | null) => void;
}

export async function handleAnalyze(deps: AnalyzeDeps): Promise<void> {
  const { contactId, setAnalyzing, setRelationshipState } = deps;
  if (!contactId) return;
  setAnalyzing(true);
  try {
    const data = await analyzeContactApi(contactId);
    setRelationshipState((data.relationshipState as RelationshipState | null) ?? null);
  } catch {
    // Best-effort
  } finally {
    setAnalyzing(false);
  }
}

export interface DraftActionDeps {
  contactId: string | undefined;
  setDraftLoading: (b: boolean) => void;
  setShowDraftSelector: (b: boolean) => void;
  setDraftVariants: (v: DraftVariant[]) => void;
}

export async function handleDraftAction(actionType: string, deps: DraftActionDeps): Promise<void> {
  const { contactId, setDraftLoading, setShowDraftSelector, setDraftVariants } = deps;
  if (!contactId) return;
  if (actionType === 'suggest_call') {
    // For "suggest call", just show a note - no email to draft
    return;
  }
  setDraftLoading(true);
  setShowDraftSelector(true);
  setDraftVariants([]);
  try {
    const data = await draftEmailApi(contactId, actionType);
    setDraftVariants(data.variants || []);
  } catch {
    // Best-effort
  } finally {
    setDraftLoading(false);
  }
}
